"use client";

import { TEST_IDS } from "@/constants/testIds";
import BugReportDialog from "@/components/dialogs/BugReportDialog";
import FeedbackDialog from "@/components/dialogs/FeedbackDialog";
import KeyboardShortcutsDialog from "@/components/dialogs/KeyboardShortcutsDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTheme } from "@/contexts/ThemeProvider";
import {
  dropdownMenuItemClasses,
  dropdownMenuItemDestructiveClasses,
} from "@/lib/constants/menu-styles";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store";
import {
  Bell,
  ChevronsUpDown,
  HelpCircle,
  Laptop,
  LogOut,
  Moon,
  Settings,
  Shield,
  Sparkles,
  Sun,
} from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { eventBus } from "@/utils/eventBus";

/**
 * NavUser - User profile component using sidebar-07 pattern
 *
 * Provides user profile, settings, notifications, files access, and
 * theme switching using the sidebar-07 pattern with SidebarMenu primitives.
 */
export function NavUser({
  notificationsDialogOpen = false,
  setNotificationsDialogOpen = () => {},
  tempChat = false,
}) {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const {
    profile: user,
    logout,
    workspaces,
    initializeWorkspaces,
  } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isBugReportDialogOpen, setIsBugReportDialogOpen] = useState(false);
  const [isKeyboardShortcutsDialogOpen, setIsKeyboardShortcutsDialogOpen] =
    useState(false);

  // Build profile object from user data
  const profile = {
    username: user?.username,
    email: user?.email,
    avatar_url: user?.avatar_url || "",
    bio: user?.bio || "No bio set",
    subscription: user?.subscription || "Free",
    full_name: user?.full_name,
    display_name: user?.settings?.profile?.display_name,
    settings: user?.settings,
  };

  // Fetch notification count when user changes
  useEffect(() => {
    if (user?.id) {
      fetchNotificationCount();
    }
  }, [user?.id]);

  // Function to fetch pending notifications count
  const fetchNotificationCount = async () => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from("notifications")
        .select("id")
        .eq("receiver_id", user.id)
        .eq("status", "pending")
        .eq("is_read", false);

      if (error) {
        console.error("Error fetching notifications:", error);
        return;
      }

      setNotificationCount(data.length);
    } catch (error) {
      console.error("Error in notification count fetch:", error);
    }
  };

  // Listen for open-settings-dialog events from memory indicator - now navigate to /settings
  const handleOpenSettingsDialog = useCallback(
    (data) => {
      const tab = data?.defaultTab || "account";
      navigate(`/settings?tab=${tab}`);
    },
    [navigate]
  );

  useEffect(() => {
    eventBus.on("open-settings-dialog", handleOpenSettingsDialog);
    return () => {
      eventBus.off("open-settings-dialog", handleOpenSettingsDialog);
    };
  }, [handleOpenSettingsDialog]);

  // Handle settings navigation - navigate to /settings page with tab
  const handleOpenSettings = useCallback(
    (tab = "account") => {
      navigate(`/settings?tab=${tab}`);
    },
    [navigate]
  );

  // Theme toggle handler
  const handleThemeToggle = () => {
    if (theme === "light") {
      setTheme("dark");
    } else if (theme === "dark") {
      setTheme("system");
    } else {
      setTheme("light");
    }
  };

  // Navigation handler
  const handleNavigate = (path) => {
    navigate(path);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      const themeKey = "theme";
      const savedTheme = localStorage.getItem(themeKey);

      // Flag intentional logout so auth listeners don't show "session expired"
      localStorage.setItem("intentional_logout", "true");

      await logout();

      localStorage.clear();

      if (savedTheme) {
        localStorage.setItem(themeKey, savedTheme);
      }

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      window.location.href = "/";
    } catch (err) {
      console.error("Error during logout:", err);
      localStorage.removeItem("intentional_logout");
      window.location.href = "/";
    }
  };

  // Open NotificationsDialog - uses prop from app-sidebar
  const handleOpenNotifications = () => {
    setNotificationsDialogOpen(true);
  };

  // Get theme icon
  const getThemeIcon = () => {
    if (theme === "dark") return <Moon className="h-5 w-5" />;
    if (theme === "system") return <Laptop className="h-5 w-5" />;
    return <Sun className="h-5 w-5" />;
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground transition-none group-data-[collapsible=icon]:justify-center"
                data-testid={TEST_IDS.SIDEBAR_USER_MENU}
              >
                <Avatar className="h-8 w-8 rounded-full">
                  <AvatarImage
                    src={profile?.avatar_url}
                    alt={profile?.full_name || profile?.email}
                  />
                  <AvatarFallback className="rounded-full">
                    {(
                      profile?.full_name?.[0] ||
                      profile?.username?.[0] ||
                      profile?.email?.[0]
                    )?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">
                    {profile?.full_name ||
                      profile?.display_name ||
                      profile?.username ||
                      profile?.email}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {profile?.settings?.subscription?.plan || "Free"}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto h-4 w-4 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 p-1 rounded-2xl shadow-lg shadow-black/10 border backdrop-blur-md bg-white/70 dark:bg-zinc-800/70 border-gray-200/70 dark:border-zinc-600/50 z-[9999]"
              side={isMobile ? "bottom" : "top"}
              align="end"
              sideOffset={8}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-3 px-3 py-2 text-left text-xs">
                  <Avatar className="h-8 w-8 rounded-full">
                    <AvatarImage
                      src={profile?.avatar_url}
                      alt={profile?.full_name || profile?.email}
                    />
                    <AvatarFallback className="rounded-full">
                      {(
                        profile?.full_name?.[0] ||
                        profile?.username?.[0] ||
                        profile?.email?.[0]
                      )?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate font-medium text-gray-900 dark:text-white">
                      {profile?.full_name ||
                        profile?.display_name ||
                        profile?.username}
                    </span>
                    <span className="truncate text-[10px] text-gray-500 dark:text-white/70">
                      {profile?.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>

              {!tempChat &&
                profile?.settings?.subscription?.plan === "Free" && (
                  <>
                    <DropdownMenuSeparator className="my-1 bg-gray-200/50 dark:bg-zinc-700/50" />
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onClick={() => {
                          setIsMenuOpen(false);
                          handleNavigate("/pricing");
                        }}
                        className={dropdownMenuItemClasses}
                      >
                        <Sparkles className="h-5 w-5" />
                        <span>Upgrade to Pro</span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </>
                )}

              {!tempChat && (
                <>
                  <DropdownMenuSeparator className="my-1 bg-gray-200/50 dark:bg-zinc-700/50" />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleOpenSettings("account");
                      }}
                      className={dropdownMenuItemClasses}
                      data-testid={TEST_IDS.SIDEBAR_SETTINGS_BUTTON}
                    >
                      <Settings className="h-5 w-5" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setIsMenuOpen(false);
                        setTimeout(() => {
                          handleOpenNotifications();
                        }, 0);
                      }}
                      className={dropdownMenuItemClasses}
                      data-testid={TEST_IDS.SIDEBAR_NOTIFICATIONS_BUTTON}
                    >
                      <Bell className="h-5 w-5" />
                      <span>Notifications</span>
                      {notificationCount > 0 && (
                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white shadow-sm">
                          {notificationCount > 9 ? "9+" : notificationCount}
                        </span>
                      )}
                    </DropdownMenuItem>
                    {/* Admin dashboard - only show for specific users */}
                    {(profile?.email === "ydavid365@gmail.com" ||
                      profile?.email === "jack@artifact.chat") && (
                      <DropdownMenuItem
                        onClick={() => {
                          setIsMenuOpen(false);
                          handleNavigate("/dashboard");
                        }}
                        className={dropdownMenuItemClasses}
                      >
                        <Shield className="h-5 w-5" />
                        <span>Admin Dashboard</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                </>
              )}

              <DropdownMenuSeparator className="my-1 bg-gray-200/50 dark:bg-zinc-700/50" />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className={dropdownMenuItemClasses}>
                  <HelpCircle className="h-5 w-5" />
                  <span className="flex-1">Help</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  className="w-48 rounded-2xl shadow-lg shadow-black/10 border backdrop-blur-md bg-white/70 dark:bg-zinc-800/70 border-gray-200/70 dark:border-zinc-600/50"
                  sideOffset={5}
                >
                  {/* Documentation - Internal Link */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      setIsMenuOpen(false);
                      handleNavigate("/docs");
                    }}
                  >
                    Documentation
                  </DropdownMenuItem>

                  {/* Support - Internal Link */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      setIsMenuOpen(false);
                      handleNavigate("/support");
                    }}
                  >
                    Support
                  </DropdownMenuItem>

                  {/* Send Feedback - Action Button */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      setIsMenuOpen(false);
                      setIsFeedbackDialogOpen(true);
                    }}
                  >
                    Send Feedback
                  </DropdownMenuItem>

                  {/* Report Bug - Action Button */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      setIsMenuOpen(false);
                      setIsBugReportDialogOpen(true);
                    }}
                  >
                    Report Bug
                  </DropdownMenuItem>

                  {/* Keyboard Shortcuts - Action Button */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      setIsMenuOpen(false);
                      setIsKeyboardShortcutsDialogOpen(true);
                    }}
                  >
                    Keyboard Shortcuts
                  </DropdownMenuItem>

                  {/* Report (Discord) - External Link */}
                  <DropdownMenuItem
                    className={dropdownMenuItemClasses}
                    onSelect={() => {
                      window.open(
                        "https://discord.com/invite/rBqn5m2p",
                        "_blank"
                      );
                    }}
                  >
                    <span className="flex-1">Report</span>
                    <span className="text-[10px] text-gray-500 dark:text-white/50">
                      ↗
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator className="my-1 bg-gray-200/50 dark:bg-zinc-700/50" />
              <DropdownMenuItem
                onClick={handleThemeToggle}
                onSelect={(e) => e.preventDefault()}
                className={dropdownMenuItemClasses}
                data-testid={TEST_IDS.SIDEBAR_THEME_TOGGLE}
              >
                {getThemeIcon()}
                <span>
                  Theme: {theme.charAt(0).toUpperCase() + theme.slice(1)}
                </span>
              </DropdownMenuItem>

              {!tempChat && (
                <>
                  <DropdownMenuSeparator className="my-1 bg-gray-200/50 dark:bg-zinc-700/50" />
                  <DropdownMenuItem
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogout();
                    }}
                    className={`${dropdownMenuItemClasses} ${dropdownMenuItemDestructiveClasses}`}
                    data-testid={TEST_IDS.SIDEBAR_LOGOUT_BUTTON}
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={isFeedbackDialogOpen}
        onOpenChange={setIsFeedbackDialogOpen}
      />

      {/* Bug Report Dialog */}
      <BugReportDialog
        open={isBugReportDialogOpen}
        onOpenChange={setIsBugReportDialogOpen}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog
        open={isKeyboardShortcutsDialogOpen}
        onOpenChange={setIsKeyboardShortcutsDialogOpen}
      />
    </>
  );
}

NavUser.propTypes = {
  notificationsDialogOpen: PropTypes.bool,
  setNotificationsDialogOpen: PropTypes.func,
  tempChat: PropTypes.bool,
};
